/**
 * @deprecated Import from a focused @agent-native/core/client/* entrypoint instead.
 * Compatibility aggregation only; new code must not import this barrel.
 */
export * from "./agent-chat/index.js";
export * from "./hooks/index.js";
export * from "./navigation/index.js";
export * from "./host/index.js";
export * from "./widgets/index.js";
export * from "./ui/index.js";

export { cn } from "@agent-native/toolkit/utils";
export {
  AgentNativeI18nProvider,
  LanguagePicker,
  getLocaleInitScript,
  localeDirection,
  normalizeLocaleCode,
  normalizeLocalePreference,
  normalizeLocalizationPreference,
  resolveLocaleFromCandidates,
  resolveLocaleFromPreference,
  useFormatters,
  useLocale,
  useT,
  DEFAULT_LOCALE,
  LOCALE_HYDRATION_GLOBAL,
  LOCALE_METADATA,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  type AgentNativeI18nCatalog,
  type AgentNativeI18nProviderProps,
  type LocaleCode,
  type LocaleHydrationPayload,
  type LocaleMessages,
  type LocaleMetadata,
  type LocalePreference,
  type LocalizationPreference,
} from "./i18n.js";
export { AgentNativeIcon } from "./components/icons/AgentNativeIcon.js";
export {
  FeatureFlagsEditor,
  evaluatedFeatureFlagValues,
  featureFlagValue,
  useFeatureFlag,
  useFeatureFlags,
  type FeatureFlagActor,
  type FeatureFlagMetadata,
  type FeatureFlagRules,
  type SetFeatureFlagInput,
} from "./feature-flags/index.js";
export { withBuilderUtmTrackingParams } from "../shared/builder-link-tracking.js";
export {
  SettingsPanel,
  SettingsTabsPage,
  SecretsSection,
  getAgentSettingsSearchTabs,
  openBuilderConnectPopup,
  useAgentSettingsTabs,
  useBuilderConnectFlow,
  useBuilderStatus,
  withBuilderConnectTrackingParams,
  type BuilderConnectFlow,
  type BuilderConnectFlowOptions,
  type BuilderConnectStartOptions,
  type BuilderStatus,
  type AgentSettingsSearchTab,
  type OpenBuilderConnectPopupOptions,
  type SecretsSectionProps,
  type SettingsPanelProps,
  type SettingsSearchEntry,
  type SettingsTabItem,
  type SettingsTabsPageProps,
} from "./settings/index.js";
export {
  DevDatabaseLink,
  type DevDatabaseLinkProps,
} from "./db-admin/DevDatabaseLink.js";
export { DbAdminPage } from "./db-admin/DbAdminPage.js";
export {
  installRouteChunkRecovery,
  reloadForStaleChunk,
  recoverFromStaleChunkError,
} from "./route-chunk-recovery.js";
export { AgentTerminal, type AgentTerminalProps } from "./terminal/index.js";
export {
  trackEvent,
  trackAgentChatLifecycle,
  trackSessionStatus,
  configureTracking,
  setTrackingContentCaptureEnabled,
  maybeStartSessionReplay,
  startSessionReplay,
  stopSessionReplay,
  getAnalyticsAnonymousId,
  getAnalyticsSessionId,
  getFirstTouchAttribution,
  setSentryUser,
  setTrackingIdentity,
  getSessionReplayContext,
  getSessionReplayUrl,
  captureError,
  captureClientException,
  // First-party, Sentry-style error capture (auto + manual API).
  AGENT_NATIVE_EXCEPTION_EVENT_NAME,
  addErrorBreadcrumb,
  captureException,
  captureMessage,
  isErrorCaptureInstalled,
  type CaptureExceptionContext,
  type CapturedExceptionEvent,
  type ClientCaptureContext,
  type AgentChatLifecycleEvent,
  type ConfigureTrackingOptions,
  type ErrorCaptureConfigOptions,
  type ExceptionBreadcrumb,
  type ExceptionLevel,
  type FirstTouchAttribution,
  type SessionReplayOptions,
  type SessionReplayContext,
  type SessionReplayLinkOptions,
  type SessionReplayStartResult,
  type SessionReplayUrlMatcher,
  type TrackingIdentityUser,
} from "./analytics.js";
export { track } from "./track.js";
export {
  useCollaborativeDoc,
  isReconcileLeadClient,
  emailToColor,
  emailToName,
  dedupeCollabUsersByEmail,
  type UseCollaborativeDocOptions,
  type UseCollaborativeDocResult,
  type CollabUser,
} from "../collab/client.js";
export { AGENT_CLIENT_ID } from "../collab/agent-identity.js";
export {
  usePresence,
  toNormalized,
  fromNormalized,
  type OtherPresence,
  type PresencePayload,
  type UsePresenceResult,
  type NormalizedPoint,
} from "../collab/presence.js";
export {
  useFollowUser,
  type UseFollowUserOptions,
  type UseFollowUserResult,
  type ViewportDescriptor,
} from "../collab/follow-mode.js";
export {
  ResourcesPanel,
  ResourceTree,
  ResourceEditor,
  DEFAULT_MCP_INTEGRATIONS,
  getDefaultMcpIntegrations,
  mergeDefaultMcpIntegrations,
  useResources,
  useResourceTree,
  useResource,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  useUploadResource,
  type Resource,
  type ResourceMeta,
  type TreeNode,
  type ResourceScope,
  type ResourceTreeProps,
  type ResourceEditorProps,
  type DefaultMcpIntegration,
} from "./resources/index.js";
export type { ResourcesPanelProps } from "./resources/ResourcesPanel.js";
export {
  HistoryTimeline,
  VersionHistoryPanel,
  useCreateResourceVersion,
  useResourceHistory,
  useResourceVersion,
  useResourceVersions,
  useRestoreResourceVersion,
  type CreateResourceVersionInput,
  type GetResourceVersionInput,
  type GetResourceVersionResult,
  type ListResourceHistoryResult,
  type ListResourceVersionsResult,
  type ResourceHistoryParams,
  type ResourceVersionsParams,
  type RestoreResourceVersionInput,
  type RestoreResourceVersionResult,
  type VersionHistoryPanelProps,
} from "./history/index.js";
export {
  ReviewCommentComposer,
  ReviewStatusBadge,
  ReviewThreadPanel,
  buildReviewThreads,
  useConsumeReviewFeedback,
  useCreateReviewComment,
  useDeleteReviewComment,
  useReplyReviewComment,
  useResolveReviewThread,
  useReviewComments,
  useReviewFeedback,
  useSendReviewThreadToAgent,
  useSetReviewStatus,
  type ConsumeReviewFeedbackInput,
  type CreateReviewCommentInput,
  type DeleteReviewCommentInput,
  type GetReviewFeedbackParams,
  type GetReviewFeedbackResult,
  type ListReviewCommentsParams,
  type ListReviewCommentsResult,
  type ReplyReviewCommentInput,
  type ResolveReviewThreadInput,
  type ReviewStatusBadgeProps,
  type ReviewCommentComposerProps,
  type ReviewThread,
  type ReviewThreadPanelProps,
  type SetReviewStatusInput,
  type SendReviewThreadToAgentInput,
} from "./review/index.js";
export {
  BuilderConnectCard,
  DefaultBuilderConnectCardView,
  ProviderReadinessBadge,
  SetupConnectionsPage,
  useBuilderConnectCardController,
  type BuilderConnectCardAction,
  type BuilderConnectCardControllerOptions,
  type BuilderConnectCardProps,
  type BuilderConnectCardRenderContext,
  type BuilderConnectCardStatus,
  type BuilderConnectCardViewModel,
  type DefaultBuilderConnectCardViewProps,
  type ProviderReadinessBadgeProps,
  type SetupConnectionsPageProps,
} from "./setup-connections/index.js";
export {
  listIntegrationEnvStatuses,
  listIntegrationStatuses,
  saveIntegrationEnvVars,
  setIntegrationEnabled,
  setupIntegration,
  disconnectManagedIntegrationInstallation,
  listManagedIntegrationInstallations,
  managedIntegrationOAuthUrl,
  managedSlackAgentManifestUrl,
  listManagedIntegrationScopes,
  saveManagedIntegrationScope,
  listManagedIntegrationBudgets,
  listManagedIntegrationMemory,
  forgetManagedIntegrationMemory,
  saveManagedIntegrationBudget,
  testManagedIntegrationInstallation,
  IntegrationClientError,
  type ClientIntegrationInstallation,
  type ClientIntegrationScope,
  type ClientIntegrationUsageBudget,
  type ClientIntegrationMemory,
  type ClientIntegrationStatus,
  type IntegrationEnvStatus,
  type SavedEnvVarsResult,
} from "./integrations/index.js";
export {
  invokeConfiguredAutomationWorkflow,
  type InvokeConfiguredAutomationWorkflowInput,
} from "./automation.js";
export {
  ChangelogDialog,
  ChangelogSettingsCard,
  useChangelogSeen,
  parseChangelog,
  type ChangelogDialogProps,
  type ChangelogSettingsCardProps,
  type ChangelogEntry,
} from "./changelog/Changelog.js";
export {
  DevOverlay,
  useDevOverlayShortcut,
  registerDevPanel,
  unregisterDevPanel,
  listDevPanels,
  subscribeDevPanels,
  useDevOption,
  clearAllDevOverlayStorage,
  devOptionKey,
  DEV_OVERLAY_STORAGE_PREFIX,
  type DevOverlayProps,
  type DevPanel,
  type DevOption,
  type DevBooleanOption,
  type DevSelectOption,
  type DevStringOption,
  type DevActionOption,
  type DevOptionValue,
} from "./dev-overlay/index.js";
export {
  ShareButton,
  ShareDialog,
  useShareButtonController,
  type ShareButtonProps,
  type ShareButtonController,
  type ShareButtonControllerOptions,
  type ShareButtonOrgMember,
  type ShareButtonOrgMemberSearch,
  type ShareButtonRole,
  type ShareButtonShare,
  type ShareButtonSharesResponse,
  type ShareButtonVisibility,
  type ShareDialogProps,
} from "./sharing/index.js";
export {
  ObservabilityDashboard,
  ThumbsFeedback,
} from "./observability/index.js";
export {
  appendRecentEdit,
  collectRecentEdits,
  publishRecentEdit,
  useRecentEdits,
  RECENT_EDITS_MAX,
  RECENT_EDIT_TTL_MS,
  type RecentEdit,
  type RecentEditDescriptor,
  type AttributedRecentEdit,
  type UseRecentEditsOptions,
} from "../collab/recent-edits.js";
export {
  useCollabUndo,
  useLocalOpUndo,
  createLocalOpUndoController,
  type UseCollabUndoOptions,
  type UseCollabUndoResult,
  type CollabUndoScope,
  type UseLocalOpUndoOptions,
  type UseLocalOpUndoResult,
  type LocalOpUndoEntry,
  type LocalOpUndoController,
  type CreateLocalOpUndoOptions,
  type UndoKeyboardOptions,
} from "../collab/undo.js";
export {
  useCollaborativeMap,
  useCollaborativeArray,
  type UseCollaborativeMapOptions,
  type UseCollaborativeMapResult,
  type UseCollaborativeArrayOptions,
  type UseCollaborativeArrayResult,
} from "../collab/client-struct.js";
export { NotificationsBell } from "./notifications/index.js";
export {
  defineBlock,
  BlockRegistry,
  registerBlocks,
  BlockRegistryProvider,
  useBlockRegistry,
  useOptionalBlockRegistry,
  BlockView,
  SchemaBlockEditor,
  markdown,
  richtext,
  introspect,
  serializeSpecBlock,
  parseSpecBlock,
  createAttrReader,
  childCodeFenceFields,
  serializeChildCodeFenceFields,
  describeBlocksForAgent,
  renderBlockVocabularyReference,
  type BlockSpec,
  type BlockPlacement,
  type BlockMdxConfig,
  type BlockAttrReader,
  type BlockRenderContext,
  type BlockReadProps,
  type BlockEditProps,
  type MdxAttrValue,
  type FieldKind,
  type FieldDescriptor,
  type MdxJsxNode,
  type MdxAttrNode,
  type SerializableBlock,
  type ParsedBlockBase,
  type BlockAgentDoc,
} from "./blocks/index.js";
